import { saneBoolean, Scope } from 'logard';
import moment from 'moment';
import { reactive } from 'vue';
import { DossierId } from '@/dossier/models/AbstractDossier';
import Dossier from '@/dossier/models/Dossier';
import { ListCache } from '@/general/utils/cache';
import { ROUTE_DATE_FORMAT } from '@/planner/constants/general';
import CommonFilterType from '@/shared/constants/CommonFilterType';
import FilterService from '@/shared/services/FilterService';
import serviceEvents, { UserLoggedOut, WorkPreparationStatusChanged } from '@/shared/services/events/serviceEvents';
import { withRawResults } from '@/shared/utils/component';
import { saneDate } from '@/shared/utils/sanitizers';
import { isPresent, present } from '@/shared/utils/typescript';
import WorkPreparationClient, { WorkPreparationSaveResult } from '../client/WorkPreparationClient';
import WorkPreparationStatus from '../constants/WorkPreparationStatus';
import WorkPreparationData from '../models/WorkPreparationData';
import WorkPreparationFilters from '../models/WorkPreparationFilters';
import WorkPreparationItem from '../models/WorkPreparationItem';
import WorkPreparationOptions from '../models/WorkPreparationOptions';
import WorkPreparationReason from '../models/WorkPreparationReason';

const COMMON_FILTERS = [CommonFilterType.OUTLET_MANDATORY, CommonFilterType.BRAND_OPTIONAL, CommonFilterType.TEAM_OPTIONAL];

export const PLANNED_FROM_PARAM = 'plannedFrom';
export const PLANNED_TILL_PARAM = 'plannedTill';
export const PLANNED_FROM_STORAGE_KEY = 'planned_from';
export const PLANNED_TILL_STORAGE_KEY = 'planned_till';
export const INCLUDE_PASSED_PARAM = 'includePassed';
export const MY_DOSSIERS_PARAM = 'myDossiers';
export const IS_CREATION_DATE_PARAM = 'isCreationDate';

class WorkPreparationService {
  private reasons = new ListCache(withRawResults(WorkPreparationClient.loadReasons));

  constructor() {
    serviceEvents.on(UserLoggedOut, () => this.reasons.clear());
  }

  async parseFilters(s: Scope): Promise<WorkPreparationFilters> {
    const commonFilters = await FilterService.getFilters(s, COMMON_FILTERS);

    const fallbackPlannedFrom = sessionStorage.getItem(PLANNED_FROM_STORAGE_KEY) ?? moment().format(ROUTE_DATE_FORMAT);
    const plannedFrom = present(s.getQueryParam(PLANNED_FROM_PARAM, saneDate, fallbackPlannedFrom));

    const fallbackPlannedTill = sessionStorage.getItem(PLANNED_TILL_STORAGE_KEY) ?? plannedFrom.clone().add(3, 'days').format(ROUTE_DATE_FORMAT);
    const plannedTill = present(s.getQueryParam(PLANNED_TILL_PARAM, saneDate, fallbackPlannedTill));

    const includePassed = present(s.getQueryParam(INCLUDE_PASSED_PARAM, saneBoolean, false));
    const myDossiers = present(s.getQueryParam(MY_DOSSIERS_PARAM, saneBoolean, false));
    const isCreationDate = present(s.getQueryParam(IS_CREATION_DATE_PARAM, saneBoolean, false));

    const options = new WorkPreparationOptions(includePassed, myDossiers, isCreationDate);

    sessionStorage.setItem(PLANNED_FROM_STORAGE_KEY, plannedFrom.format(ROUTE_DATE_FORMAT));
    sessionStorage.setItem(PLANNED_TILL_STORAGE_KEY, plannedTill.format(ROUTE_DATE_FORMAT));
    return new WorkPreparationFilters(commonFilters, plannedFrom, plannedTill, options);
  }

  async loadItems(dossier: Dossier): Promise<Array<WorkPreparationItem>> {
    if (dossier.workPreparationStatus) {
      return WorkPreparationClient.loadAll(dossier.id);
    } else {
      return [];
    }
  }

  loadReasons(): Promise<Array<WorkPreparationReason>> {
    return this.reasons.get();
  }

  async update(item: WorkPreparationItem, data: WorkPreparationData, dossierId: DossierId): Promise<void> {
    if (data.status === WorkPreparationStatus.TO_DO) await this.clear(item, dossierId);
    else await this.save(item, data, dossierId);
  }

  async save(item: WorkPreparationItem, data: WorkPreparationData, dossierId: DossierId): Promise<void> {
    const result = await this.doSave(item, data, dossierId);
    item.updateWith(result.item);
    await serviceEvents.fire(new WorkPreparationStatusChanged(dossierId, result.status));
  }

  private doSave(item: WorkPreparationItem, data: WorkPreparationData, dossierId?: DossierId): Promise<WorkPreparationSaveResult> {
    if (isPresent(item.id)) {
      return WorkPreparationClient.update(item.id, data);
    } else {
      return WorkPreparationClient.create(data, present(dossierId, 'no dossier or dossierId present'));
    }
  }

  async clear(item: WorkPreparationItem, dossierId: DossierId): Promise<void> {
    const id = present(item.id);
    const status = await WorkPreparationClient.remove(id);
    await serviceEvents.fire(new WorkPreparationStatusChanged(dossierId, status));
    item.clear();
  }
}

export default reactive(new WorkPreparationService());